import React from 'react';
import { Box, Typography, Paper, Tooltip, IconButton, List, ListItem, Collapse, Chip } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0.5),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TeamTreeItem = ({ 
  team, 
  users = [], 
  teamChildren, 
  onEditTeam, 
  onDeleteTeam, 
  onManageTeamMembers,
  userPermissions = {},
  expanded,
  onToggle
}) => {
  // Get users belonging to this team
  const teamManager = users.find(user => user.id === team.manager);
  const isExpanded = expanded.includes(team.id.toString());
  
  return (
    <>
      <StyledListItem
        sx={{ pl: 2 }}
        onClick={() => onToggle(team.id.toString())}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {teamChildren?.length > 0 ? (
              isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />
            ) : (
              <Box sx={{ width: 24 }} /> // Empty spacer when no children
            )}
            <Box 
              sx={{ 
                width: 4, 
                height: 24, 
                borderRadius: 1, 
                mx: 1 
              }} 
            />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {team.name}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                Members: {team.members_count} • 
                Sub-Teams: {teamChildren?.length || 0} •
                {teamManager ? ` Manager: ${teamManager.name}${teamManager.designation ? ` (${teamManager.designation})` : ''}` : ' No Manager'}
              </Typography>
              {team.tags && Array.isArray(team.tags) && team.tags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {team.tags.map((tag, index) => (
                    tag.includes(',') ?
                      tag.split(',').map((subTag, subIndex) => (
                        <Chip 
                          key={`${index}-${subIndex}`} 
                          label={subTag.trim()} 
                          size="small" 
                          variant="outlined" 
                          color="primary"
                        />
                      ))
                    :
                    <Chip 
                      key={index} 
                      label={tag} 
                      size="small" 
                      variant="outlined" 
                      color="primary"
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
          
          {userPermissions.canEdit && (
            <Box sx={{ display: 'flex' }}>
              <Tooltip title="Manage Team Members">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onManageTeamMembers(team.id);
                  }}
                >
                  <GroupIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Team">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditTeam(team);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Team">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteTeam(team.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </StyledListItem>
      {teamChildren?.length > 0 && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List sx={{ pl: 4, borderLeft: '1px dashed rgba(0, 0, 0, 0.12)', ml: 1 }}>
            {teamChildren.map(childTeam => (
              <TeamTreeItem
                key={childTeam.id}
                team={childTeam}
                users={users}
                teamChildren={childTeam.children || []}
                onEditTeam={onEditTeam}
                onDeleteTeam={onDeleteTeam}
                onManageTeamMembers={onManageTeamMembers}
                userPermissions={userPermissions}
                expanded={expanded}
                onToggle={onToggle}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

// Build hierarchical team structure
const buildTeamHierarchy = (teams) => {
  // Create map of teams by ID for quick lookup
  const teamsMap = teams.reduce((map, team) => {
    team.children = [];
    map[team.id] = team;
    return map;
  }, {});
  
  // Create hierarchy by assigning children to parent teams
  const rootTeams = [];
  
  teams.forEach(team => {
    if (team.parent_team) {
      const parent = teamsMap[team.parent_team];
      if (parent) {
        parent.children.push(teamsMap[team.id]);
      } else {
        rootTeams.push(teamsMap[team.id]);
      }
    } else {
      rootTeams.push(teamsMap[team.id]);
    }
  });
  
  return rootTeams;
};

const TeamHierarchyVisualizer = ({ 
  teams = [], 
  users = [], 
  onEditTeam, 
  onDeleteTeam, 
  onManageTeamMembers,
  userRole = 'AG' // Default to Agent (view-only)
}) => {
  // Determine user permissions based on role
  const userPermissions = {
    canEdit: userRole === 'AD', // Only Admin can edit teams
    canManageMembers: userRole === 'AD' || userRole === 'MG', // Admin and Managers can manage team members
  };
  
  // State to track expanded items
  const [expanded, setExpanded] = React.useState([]);
  
  // Handle toggle expansion
  const handleToggle = (nodeId) => {
    setExpanded(prev => 
      prev.includes(nodeId)
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };
  
  // Build team hierarchy
  const hierarchicalTeams = buildTeamHierarchy(teams);
  
  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AccountTreeIcon sx={{ mr: 1 }} />
        <Typography variant="h6">Team Hierarchy</Typography>
      </Box>
      
      {hierarchicalTeams.length > 0 ? (
        <List
          sx={{ 
            flexGrow: 1, 
            maxWidth: '100%',
            overflowY: 'auto',
            maxHeight: '600px'
          }}
        >
          {hierarchicalTeams.map(team => (
            <TeamTreeItem
              key={team.id}
              team={team}
              users={users}
              teamChildren={team.children}
              onEditTeam={onEditTeam}
              onDeleteTeam={onDeleteTeam}
              onManageTeamMembers={onManageTeamMembers}
              userPermissions={userPermissions}
              expanded={expanded}
              onToggle={handleToggle}
            />
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No teams have been created yet.
        </Typography>
      )}
    </Paper>
  );
};

export default TeamHierarchyVisualizer;
